import { FC, useMemo } from 'react';
import styles from './index.module.scss';
import { Table } from '@bfe/moly';
import { ColumnDef } from '@tanstack/react-table';
import cls from 'classnames';
import { formatKMB, gt, lt, toFixed, openLink } from '@web3/base-utils';
import { scTrack } from '@web3/base-tracking';
import { useFm, useScreenType, useParams } from '@web3/base-hooks';
import { Event, Page } from '@/config/track-config';

import EthSvg from '@/assets/imgs/eth.svg';

interface IProps {
  list: NFTProType[];
}

export type NFTProType = {
  collectionName: string;
  volumn: string;
  changePercent: string;
  floorPrice: string;
  contractAddress: string;
  chainCode: string;
  logoUrl: string;
};

export const NFTProList: FC<IProps> = ({ list }) => {
  const { locale } = useParams();
  const fm = useFm();
  const { isMobile } = useScreenType();

  //✅ GOOD: This will not cause an infinite loop of re-renders
  // because `columns` is a stable reference
  const columns: ColumnDef<NFTProType>[] = useMemo(
    () => [
      {
        accessorKey: 'collectionName',
        header: () => <div className="pl-[0px]">{fm('collection')}</div>,
        cell: ({ row }) => (
          <div className={cls(styles.collectionName, 'pl-[0px]')}>
            <img src={list[row.index].logoUrl} alt="img" />
            <span
              className="text-ellipsis overflow-hidden max-w-[150px]"
              title={row.getValue('collectionName')}
            >
              {row.getValue('collectionName')}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'volumn',
        header: () => <div className="pl-[0px]">{fm('tradingValue')}</div>,
        cell: ({ row }) => {
          const volumn: string = row.getValue('volumn');
          return (
            <div className={cls('flex gap-1 pl-[0px]', styles.chainName)}>
              <EthSvg />
              {formatKMB(toFixed(volumn, 4), 4)}
            </div>
          );
        },
      },
      {
        accessorKey: 'changePercent',
        header: () => <div className="pr-[0px]">{fm('change24H')}</div>,
        cell: ({ row }) => {
          const data: string = row.getValue('changePercent');
          const changeNum = data.replace('%', '');
          const isPos = gt(changeNum, 0);
          const isNeg = lt(changeNum, 0);
          return (
            <div
              className={cls({
                green: isPos,
                red: isNeg,
              })}
            >
              {data}
            </div>
          );
        },
      },
      {
        accessorKey: 'floorPrice',
        header: () => (
          <div className="pr-[0px] text-right">{fm('word_floor_price')}</div>
        ),
        cell: ({ row }) => {
          const floorPrice: string = row.getValue('floorPrice');
          return (
            <div
              className={cls(
                'flex gap-1 pr-[0px] justify-end',
                styles.chainName
              )}
            >
              <EthSvg />
              {floorPrice === '-' ? '-' : toFixed(floorPrice, 4)}
            </div>
          );
        },
      },
    ],
    []
  );

  const onRowClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    rowId: string
  ) => {
    scTrack(Event.Web3UserClick, {
      page_name: Page.Home,
      area: 'Marketplace',
      button_name: 'NFT',
      params: rowId,
    });
    const row = list[Number(rowId)];
    const url = `/${locale}/web3/nft-pro/collection/${row.chainCode}/${row.contractAddress}`;
    if (isMobile) {
      openLink(url);
      return;
    }
    window.open(url);
  };

  return (
    <div className={styles.inscriptionList}>
      <Table
        columns={columns}
        data={list || []}
        isLite={false}
        // showRowSelection={false}
        rowSplitStyle="none"
        cellSpaceStyle="large"
        onRowClick={onRowClick}
      />
    </div>
  );
};
